import { useAtomValue, useSetAtom } from 'jotai';
import {
  competitionAverageCompetitionIdsToFilterByAtom,
  competitionAverageSeasonIdsToFilterByAtom,
} from '@/atoms/filters/player/playerCompetitionAverageFilters';
import { playerLeagueAverageRadarFilterDebounceObject } from '@/atoms/queries/player/playerLeagueAverageRadar';
import { unwrappedSelectedPlayerTemplatePositionGroupAtom } from '@/atoms/radar';
import { DEFAULT_DEBOUNCE_MS } from '@/consts/api';
import { useIsCompetitionAverageMode } from '@/hooks/useIsCompetitionAverageMode';
import { toObject } from '@/utils/object';
import { useDeepEffect } from '../useDeepEffect';

export const useSetPlayerLeagueAverageDebouncedFilterParams = (debounceTimeout: number = DEFAULT_DEBOUNCE_MS) => {
  const isCompetitionAverageMode = useIsCompetitionAverageMode();
  const competitionIds = useAtomValue(competitionAverageCompetitionIdsToFilterByAtom);
  const seasonIds = useAtomValue(competitionAverageSeasonIdsToFilterByAtom);
  const positionGroup = useAtomValue(unwrappedSelectedPlayerTemplatePositionGroupAtom);

  const { debouncedValueAtom: leagueAverageRadarFilterAtom, timeoutDelayAtom } =
    playerLeagueAverageRadarFilterDebounceObject;
  const setLeagueAverageRadarFilter = useSetAtom(leagueAverageRadarFilterAtom);
  const setTimeoutDelay = useSetAtom(timeoutDelayAtom);

  useDeepEffect(() => {
    // null causes hasEmptyInFilters to short-circuit, so no request fires
    if (!isCompetitionAverageMode) {
      setLeagueAverageRadarFilter(null);
      return;
    }

    if (!positionGroup) return;

    // No competition resolved yet — fire immediately rather than debounce.
    if (!competitionIds.length) setTimeoutDelay(0);

    setLeagueAverageRadarFilter({
      in: {
        competition_id: competitionIds,
        ...toObject('season_id', seasonIds),
      },
      eq: { positionGroup },
    });

    // Once a competition resolves, debounce subsequent selection changes.
    if (competitionIds.length) setTimeoutDelay(debounceTimeout);
  }, [isCompetitionAverageMode, competitionIds, seasonIds, positionGroup]);
};
